import { addToast, Button, useMobile } from '@octano/global-ui';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useUserState from '../../../../../../hooks/useUserState';
import { PermissionName } from '../../../../../../types/Auth';
import { ExcelRenderSideEnum } from '../../../../../../types/Reports';
import { downloadFromBlob } from '../../../../../../utils/blob';
import GeneratingFileModal from '../GeneratingFileModal';
import NoResultsModal from '../NoResultsModal';
import SelectPeriodSectionModal from '../SelectPeriodSectionModal';
import { createReportDownloadHandler } from './handlerCreator';

enum ModalType {
  NONE,
  MODAL_SELECTOR,
  GENERATING_FILE,
  NO_RESULTS,
}

interface IReportAuditOfAttendanceActionsProps {
  downloadUrl: string;
  renderSide: ExcelRenderSideEnum;
}

const HTTP_STATUS_NO_CONTENT = 204;

export default function ReportAuditOfAttendanceActions({
  downloadUrl,
  renderSide,
}: IReportAuditOfAttendanceActionsProps) {
  const { isAuthorizedTo } = useUserState();
  const { t } = useTranslation();
  const isMobile = useMobile();

  const [openedModal, setOpenedModal] = useState<ModalType>(ModalType.NONE);
  const fileName = useRef('');

  const handleReportDownload = createReportDownloadHandler({
    beforeDownload: () => setOpenedModal(ModalType.GENERATING_FILE),
    afterDownload: ({ status }) => {
      if (status === HTTP_STATUS_NO_CONTENT) {
        setOpenedModal(ModalType.NO_RESULTS);
      } else {
        setOpenedModal(ModalType.NONE);
      }
    },
    onSuccess: (data: Blob) => {
      if (data.size > 0) {
        const blob = new Blob([data], { type: 'vnd.ms-excel' });
        downloadFromBlob(blob, fileName.current + '.xlsx');
      }
    },
    onError: () => {
      addToast({
        icon: 'error',
        color: 'danger',
        text: t(`reports.errors.errorGeneratingFile`),
      });
    },
  });

  const handleParamsSelection = (periodId: number, sectionId: number) => {
    const reportName = t(`reports.auditOfAttendance.fileNameTitle`);
    fileName.current = `${reportName}-period-${periodId}-section-${sectionId}`;
    handleReportDownload(downloadUrl, { sectionId, periodId }, renderSide);
  };

  return (
    <>
      <NoResultsModal
        isOpen={openedModal === ModalType.NO_RESULTS}
        onConfirm={() => setOpenedModal(ModalType.NONE)}
        texts={{
          title: t('reports.auditOfAttendance.noResults.title'),
          description: t('reports.auditOfAttendance.noResults.description'),
        }}
      />

      <GeneratingFileModal isOpen={openedModal === ModalType.GENERATING_FILE} />

      <SelectPeriodSectionModal
        isOpen={openedModal === ModalType.MODAL_SELECTOR}
        modalTitle={t('reports.auditOfAttendance.title')}
        onConfirm={handleParamsSelection}
        onCancel={() => setOpenedModal(ModalType.NONE)}
      />

      <div className="d-flex flex-wrap align-items-center " style={{ gap: 12 }}>
        <Button
          text={t(`common.actions.download`)}
          icon="download"
          size="sm"
          outlined
          style={{ width: isMobile ? '90%' : 123 }}
          disabled={!isAuthorizedTo([PermissionName.REPORTS])}
          onClick={() => setOpenedModal(ModalType.MODAL_SELECTOR)}
        />
      </div>
    </>
  );
}
